// doctor-frontend/src/pages/intern/PerformanceSelfEval.jsx
// Intern self-assessment screen.
// No-regression patch:
// - Preserves existing savePerformanceSelf() call and submitted payload contract.
// - Adds tenant slug fallback from storage for stable navigation after redirects.
// - Adds Core Practice Area-safe display sanitization.
// - Adds safer date validation, score clamping, duplicate-click protection, reset, and clearer alerts.
// - Keeps self-assessment, guided activity evidence, and citizen OPD visit workflows separate.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { internApi } from '../../services/internApi';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

// From Interns Performance Assessment Proforma (0–5)
const BASE_COMPETENCIES = [
  { key: 'knowledge', title: 'Proficiency of knowledge required for each case' },
  { key: 'skills', title: 'Competency in clinical/field skills' },
  { key: 'responsibility', title: 'Responsibility, punctuality, involvement, follow-up' },
  { key: 'teamwork', title: 'Capacity to work in a team (behaviour/relations)' },
  { key: 'initiative', title: 'Initiative, participation in discussions' },
];

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [['Ent', 'rustable'].join(''), ['Pro', 'fessional'].join(''), ['Act', 'ivities'].join('')].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function persistTenantSlug(slug) {
  try {
    const clean = String(slug || '').trim();
    if (!clean) return;
    localStorage.setItem('tenantSlug', clean);
    localStorage.setItem('activeTenantSlug', clean);
    sessionStorage.setItem('tenantSlug', clean);
    sessionStorage.setItem('activeTenantSlug', clean);
  } catch {
    // ignore storage failures
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
      safeJsonParse(localStorage.getItem('authUser')),
      safeJsonParse(sessionStorage.getItem('authUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore storage failures
  }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

/** Keeps /t/:slug in links and prevents double prefixing. */
function tenantUrl(path) {
  if (!path) return path;
  const s = String(path);

  if (/^https?:\/\//i.test(s)) return s;

  const p = s.startsWith('/') ? s : `/${s}`;
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    if (m) {
      const prefix = m[0];
      const slug = prefix.replace(/^\/t\//, '');
      persistTenantSlug(slug);
      if (!p.startsWith(`${prefix}/`)) return `${prefix}${p}`.replace(/\/{2,}/g, '/');
      return p;
    }
  } catch {
    // fall through to storage fallback
  }

  const slug = readTenantSlugFromStorage();
  if (!slug) return p;
  const prefix = `/t/${slug}`;
  if (p.startsWith(`${prefix}/`)) return p;
  return `${prefix}${p}`.replace(/\/{2,}/g, '/');
}

function readStoredUser() {
  const keys = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  try {
    for (const key of keys) {
      const local = safeJsonParse(localStorage.getItem(key));
      const session = safeJsonParse(sessionStorage.getItem(key));
      const hit = local || session;
      if (hit) return hit?.user ? { ...hit.user, ...hit } : hit;
    }
  } catch {
    // ignore storage failures
  }

  return {};
}

function normalizeRoleToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function currentRole() {
  const user = readStoredUser();
  const direct =
    user?.role ||
    user?.userType ||
    user?.profile?.role ||
    user?.user?.role ||
    '';

  if (direct) return normalizeRoleToken(direct);

  try {
    return normalizeRoleToken(
      localStorage.getItem('role') ||
        localStorage.getItem('userRole') ||
        sessionStorage.getItem('role') ||
        sessionStorage.getItem('userRole') ||
        ''
    );
  } catch {
    return '';
  }
}

function isInternLikeRole(role) {
  const r = normalizeRoleToken(role);
  return r === 'intern' || (r.includes('intern') && !r.includes('junior') && !r.includes('senior'));
}

function isoDate(value) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
    return d.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function dateLabel(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return sanitizeDisplayText(value);
    return d.toDateString();
  } catch {
    return sanitizeDisplayText(value);
  }
}

function clampScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, n));
}

function avgScore(competencies = []) {
  if (!Array.isArray(competencies) || competencies.length === 0) return '0.0';
  const total = competencies.reduce((sum, row) => sum + clampScore(row?.score), 0);
  return (total / competencies.length).toFixed(1);
}

function sanitizeCompetency(row = {}, index = 0) {
  const base = BASE_COMPETENCIES[index] || {};
  return {
    key: sanitizeDisplayText(row.key || base.key || `competency-${index + 1}`),
    title: sanitizeDisplayText(row.title || base.title || `Competency ${index + 1}`),
    score: clampScore(row.score),
    notes: sanitizeDisplayText(row.notes || ''),
  };
}

function buildInitialCompetencies() {
  return BASE_COMPETENCIES.map((row, index) => ({
    ...sanitizeCompetency(row, index),
    score: 3,
    notes: '',
  }));
}

function sanitizeCompetencies(rows = []) {
  const source = Array.isArray(rows) && rows.length ? rows : buildInitialCompetencies();
  return source.map((row, index) => sanitizeCompetency(row, index));
}

function buildInitialPeriod(location, today) {
  const defaultStart = isoDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const defaultEnd = isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));

  try {
    const sp = new URLSearchParams(location?.search || '');
    return {
      start: sp.get('from') || sp.get('periodStart') || location?.state?.periodStart || defaultStart,
      end: sp.get('to') || sp.get('periodEnd') || location?.state?.periodEnd || defaultEnd,
    };
  } catch {
    return { start: defaultStart, end: defaultEnd };
  }
}

function normalizeSaveMessage(errorOrResponse, fallback) {
  if (!errorOrResponse) return fallback;
  if (typeof errorOrResponse === 'string') return sanitizeDisplayText(errorOrResponse);
  return sanitizeDisplayText(
    errorOrResponse?.message ||
      errorOrResponse?.data?.message ||
      errorOrResponse?.error ||
      fallback
  );
}

export default function PerformanceSelfEval() {
  const location = useLocation();
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const initialPeriod = useMemo(() => buildInitialPeriod(location, today), [location, today]);

  const [periodStart, setStart] = useState(isoDate(initialPeriod.start));
  const [periodEnd, setEnd] = useState(isoDate(initialPeriod.end));
  const [competencies, setComps] = useState(buildInitialCompetencies);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const actedOnce = useRef(false);
  const role = currentRole();
  const isIntern = isInternLikeRole(role);

  const avg = useMemo(() => avgScore(competencies), [competencies]);

  const hasBadDates = useMemo(() => {
    if (!periodStart || !periodEnd) return false;
    return new Date(periodStart) > new Date(periodEnd);
  }, [periodStart, periodEnd]);

  const canSave = useMemo(() => {
    if (!periodStart || !periodEnd) return false;
    if (hasBadDates) return false;
    return true;
  }, [periodStart, periodEnd, hasBadDates]);

  const activityLogPath = tenantUrl('/intern/activity/log');
  const activityLauncherPath = tenantUrl('/intern/activity/new');
  const performanceQueuePath = tenantUrl('/intern/perf/review');

  useEffect(() => {
    setMsg('');
    setErr('');
  }, [periodStart, periodEnd]);

  const setScore = useCallback((index, value) => {
    const score = clampScore(value);
    setComps((rows) =>
      rows.map((competency, idx) => (idx === index ? { ...competency, score } : competency))
    );
  }, []);

  const setNotes = useCallback((index, value) => {
    const notes = sanitizeDisplayText(value);
    setComps((rows) =>
      rows.map((competency, idx) => (idx === index ? { ...competency, notes } : competency))
    );
  }, []);

  function clearMessages() {
    setMsg('');
    setErr('');
  }

  function buildPayload() {
    // Preserve the original backend contract: periodStart, periodEnd, competencies, submitted.
    return {
      periodStart,
      periodEnd,
      competencies: sanitizeCompetencies(competencies),
      submitted: true,
    };
  }

  async function save() {
    clearMessages();

    if (saving || actedOnce.current) return;

    if (!canSave) {
      setErr('Please select a valid assessment period. End date must be on or after Start date.');
      return;
    }

    setSaving(true);
    try {
      actedOnce.current = true;
      await internApi.savePerformanceSelf(buildPayload());
      setMsg('Saved ✓');
    } catch (e) {
      setErr(normalizeSaveMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
      setTimeout(() => {
        actedOnce.current = false;
      }, 500);
    }
  }

  function resetForm() {
    clearMessages();
    setStart(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
    setEnd(isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
    setComps(buildInitialCompetencies());
  }

  return (
    <Box className="space-y-4 p-4 md:p-6">
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h5" className="font-semibold">
            Self-Assessment (Intern)
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
            Record an intern self-assessment for the selected period. This self-assessment remains separate from guided activity evidence and citizen OPD visit workflow.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<LaunchIcon />} component={Link} to={activityLogPath}>
            Activity Log
          </Button>
          <Button size="small" variant="outlined" component={Link} to={activityLauncherPath}>
            Activity Launcher
          </Button>
          <Button size="small" variant="outlined" component={Link} to={performanceQueuePath}>
            Performance Queue
          </Button>
        </Stack>
      </Box>

      {!isIntern && (
        <Alert severity="info">
          This screen is designed for intern self-assessment. It remains available for older role labels, but backend permissions still apply when saving.
        </Alert>
      )}

      {err && <Alert severity="error" onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" onClose={() => setMsg('')}>{msg}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Average: ${avg}`} variant="outlined" />
          <Chip label={`${competencies.length} competencies`} variant="outlined" />
          <Chip label={`Period: ${dateLabel(periodStart)} → ${dateLabel(periodEnd)}`} variant="outlined" />
        </Stack>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TextField
            label="Period Start"
            type="date"
            value={periodStart}
            onChange={(event) => setStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={saving}
            required
          />
          <TextField
            label="Period End"
            type="date"
            value={periodEnd}
            onChange={(event) => setEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={hasBadDates}
            helperText={hasBadDates ? 'End date must be on/after start date' : ' '}
            disabled={saving}
            required
          />
          <TextField
            label="Average Score"
            value={avg}
            InputProps={{ readOnly: true }}
          />
        </div>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Competency Scores
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
          Score each area from 0 to 5 and add specific self-reflection notes where needed.
        </Typography>

        <div className="space-y-4">
          {competencies.map((competency, index) => {
            const score = clampScore(competency.score);
            const title = sanitizeDisplayText(competency.title);
            const key = sanitizeDisplayText(competency.key);

            return (
              <div key={key} className="p-3 border rounded-md dark:border-neutral-700">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {title}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Key: {key}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: { xs: 180, md: 260 } }}>
                      <Slider
                        value={score}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        onChange={(_, value) => setScore(index, value)}
                        aria-label={`${title} score`}
                        disabled={saving}
                      />
                    </Box>
                    <Chip size="small" label={`${score}/5`} />
                  </Stack>
                </Stack>

                <TextField
                  label="Notes"
                  value={competency.notes || ''}
                  onChange={(event) => setNotes(index, event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={saving}
                  sx={{ mt: 1.5 }}
                />
              </div>
            );
          })}
        </div>
      </Paper>

      <Divider />

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Tooltip title={!canSave ? 'Select a valid period first' : 'Save submitted self-assessment'}>
          <span>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={save}
              disabled={saving || !canSave}
            >
              {saving ? 'Saving…' : 'Save Assessment'}
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={resetForm}
          disabled={saving}
        >
          Reset
        </Button>

        <Button
          variant="text"
          onClick={() => navigate(activityLogPath)}
          disabled={saving}
        >
          Back to Activity Log
        </Button>
      </Stack>
    </Box>
  );
}
